import { AlertCircle, X } from "lucide-react";
import type { BuyerLeadSource } from "@/api/buyers";
import {
  Button,
  Dropzone,
  FormField,
  Textarea,
} from "@/design-system/components/ui";
import { SMART_IMPORT_MAX_FILE_BYTES } from "@/hooks/buyers/useSmartImport";
import { smartImportCopy } from "./smartImportCopy";
import { SmartImportPickers } from "./SmartImportPickers";
import type { SmartImportPlacement } from "./smartImportReview";

/**
 * The landing: a file or a paste, plus where the buyers land (D10, D16).
 *
 * <p>The two inputs are EXCLUSIVE — choosing one clears the other — because
 * every request in this flow carries exactly one source part, and a screen
 * showing both filled would leave the operator to guess which one the import
 * read.</p>
 *
 * <p>The 10 MB refusal is client-side as well as server-side, because the
 * server's is a 413 written by the web server rather than the API: it arrives
 * with no envelope and no sentence. Refusing here means the operator is told
 * what the limit is and what to do about it before they wait for an upload
 * that cannot succeed.</p>
 */

export const SMART_IMPORT_ACCEPT = ".csv,.xlsx,.xlsm,.xls";

export interface SmartImportUploadProps {
  file: File | null;
  onFile: (file: File | null) => void;
  pastedText: string;
  onPastedText: (text: string) => void;
  placement: SmartImportPlacement;
  onPlacement: (placement: SmartImportPlacement) => void;
  onAdvertisementSource?: (source: BuyerLeadSource | null) => void;
  onAnalyze: () => void;
  analyzing?: boolean;
  error?: string | null;
}

export function SmartImportUpload({
  file,
  onFile,
  pastedText,
  onPastedText,
  placement,
  onPlacement,
  onAdvertisementSource,
  onAnalyze,
  analyzing,
  error,
}: SmartImportUploadProps) {
  const hasSource = file != null || pastedText.trim().length > 0;

  return (
    <div className="grid gap-space-4" data-testid="smart-import-upload">
      <p className="text-small text-gray-mid">{smartImportCopy.uploadDescription}</p>

      <FormField label={smartImportCopy.fileLabel} helperText={smartImportCopy.fileHint}>
        <div>
          <Dropzone
            accept={SMART_IMPORT_ACCEPT}
            maxSizeBytes={SMART_IMPORT_MAX_FILE_BYTES}
            disabled={analyzing}
            inputTestId="smart-import-file"
            data-testid="smart-import-dropzone"
            onFiles={(files) => {
              const next = files[0] ?? null;
              if (!next) return;
              onFile(next);
              // Exclusive: a pasted block left behind would be a second source
              // the request has no part for.
              onPastedText("");
            }}
          />
          {file && (
            <p
              className="mt-space-2 flex items-center gap-space-2 text-small text-onyx"
              data-testid="smart-import-file-name"
            >
              <span className="font-medium break-all">{file.name}</span>
              <Button
                variant="text"
                size="sm"
                disabled={analyzing}
                onClick={() => onFile(null)}
                aria-label={`Remove ${file.name}`}
              >
                <X size={14} strokeWidth={1.5} aria-hidden />
                Remove
              </Button>
            </p>
          )}
        </div>
      </FormField>

      <FormField label={smartImportCopy.pasteLabel} helperText={smartImportCopy.pasteHint}>
        <Textarea
          data-testid="smart-import-paste"
          rows={6}
          disabled={analyzing || file != null}
          placeholder={smartImportCopy.pastePlaceholder}
          value={pastedText}
          onChange={(e) => onPastedText(e.target.value)}
        />
      </FormField>

      <SmartImportPickers
        placement={placement}
        onChange={onPlacement}
        onAdvertisementSource={onAdvertisementSource}
        disabled={analyzing}
      />

      {error && (
        <p
          role="alert"
          className="flex items-start gap-space-2 text-small text-status-error"
          data-testid="smart-import-upload-error"
        >
          <AlertCircle size={14} strokeWidth={1.5} aria-hidden className="mt-[2px]" />
          <span>{error}</span>
        </p>
      )}

      <div>
        <Button
          data-testid="smart-import-analyze"
          disabled={!hasSource || analyzing}
          onClick={onAnalyze}
        >
          {analyzing ? smartImportCopy.analyzing : smartImportCopy.analyzeAction}
        </Button>
      </div>
    </div>
  );
}
